import React, {
  FC, useCallback, useEffect,
} from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import ReactPlayerAutoplay from 'components/ReactPlayerAutoplay';
import 'objectFitPolyfill';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';
import ZendeskTrigger from '../ZendeskTrigger';

const StyledLink = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;
`;

const StyledExternalLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
`;

const ImageContainer = styled.div<{ $clickable?: boolean }>`
  width: 100%;
  height: 100%;
  justify-content: center;
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
`;

const ImageBanner = styled.img<{ $objectFit: string }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) => $objectFit};
`;

const StyledIframe = styled.iframe`
  height: 100%;
  width: 100%;
  user-select: all;
  pointer-events: all;
`;

interface ChatWrapperProps {
  chatroomId: string;
  chatroomName: string;
}

const ChatWrapper: FC<ChatWrapperProps> = React.memo(({ chatroomId, chatroomName, children }) => {
  const { joinPublicChatroom } = useTwilioEventFunctions();

  const onChatClick = useCallback(async () => {
    if (!chatroomId) return;
    await joinPublicChatroom(chatroomId);
  }, [joinPublicChatroom, chatroomId]);

  return (
    <ImageContainer onClick={onChatClick} $clickable>
      {children}
    </ImageContainer>
  );
});

interface ImageWrapperProps {
  redirectUrl: string;
  redirectExternal: boolean;
  chatroomId: string;
  chatroomName: string;
}

const ImageWrapper: FC<ImageWrapperProps> = ({
  redirectUrl,
  redirectExternal,
  chatroomId,
  chatroomName,
  children,
}) => {
  if (chatroomId) {
    return (
      <ChatWrapper chatroomId={chatroomId} chatroomName={chatroomName}>
        {children}
      </ChatWrapper>
    );
  }

  const Image = <ImageContainer>{children}</ImageContainer>;
  if (redirectUrl) {
    return (redirectExternal
      ? <StyledExternalLink href={redirectUrl} target="_blank" rel="noopener noreferrer">{Image}</StyledExternalLink>
      : <StyledLink to={redirectUrl}>{Image}</StyledLink>
    );
  }
  return Image;
};

export interface BannerSwitcherProps {
  activeIndex: number;
  type: 'IMAGE' | 'VIDEO' | 'LINK' | 'IFRAME' | 'ZENDESK';
  src: string;
  redirectUrl: string;
  index: number;
  onVideoPlay: () => void;
  onVideoStop: () => void;
  htmlId?: string;
  autoPlay?: boolean;
  muted?: boolean;
  redirectExternal?: boolean;
  chatroomId?: string;
  chatroomName?: string;
  objectFit?: string;
}

const BannerSwitcher: FC<BannerSwitcherProps> = ({
  activeIndex,
  type,
  src,
  redirectUrl,
  index,
  onVideoPlay,
  onVideoStop,
  htmlId,
  chatroomId,
  chatroomName,
  autoPlay = false,
  muted = false,
  redirectExternal = false,
  objectFit = 'contain',
}) => {
  useEffect(() => {
    window.objectFitPolyfill();
  }, []);

  switch (type) {
    case 'IMAGE': return (
      <ImageWrapper
        chatroomId={chatroomId}
        chatroomName={chatroomName}
        redirectExternal={redirectExternal}
        redirectUrl={redirectUrl}
      >
        <ImageBanner src={src} id={htmlId} $objectFit={objectFit} />
      </ImageWrapper>
    );
    case 'VIDEO': return (
      <ReactPlayerAutoplay
        videoUrl={src}
        videoPlayCallback={onVideoPlay}
        videoStopCallback={onVideoStop}
        autoPlay={autoPlay}
        displayed={activeIndex === index}
        defaultPropsOverride={{
          id: htmlId,
          style: { zIndex: 100 },
        }}
        muted={muted ?? true}
      />
    );
    case 'LINK': return (
      redirectExternal
        ? <StyledExternalLink id={htmlId} href={redirectUrl} target="_blank" rel="noopener noreferrer" />
        : <StyledLink id={htmlId} to={redirectUrl} />
    );
    case 'IFRAME': return (
      <StyledIframe
        id={htmlId}
        src={src}
        allow="fullscreen"
      />
    );
    case 'ZENDESK': return <ZendeskTrigger />;
    default: return null;
  }
};

export default BannerSwitcher;
